import type { Doctor, DoctorTreatment } from "@/features/clinic/types/doctor";
import type { Treatment } from "@/features/clinic/types/treatment";

import { getMockMarket, type MockMarket } from "../market";

import {
  createId,
  createPastTimestamp,
  createAvatarUrl,
  pickMultiple,
} from "./base";

interface DoctorConfig {
  description: string;
  specialization: string;
}

const STORAX_DOCTORS: DoctorConfig[] = [
  {
    description:
      "Board-certified aesthetic physician with 12+ years experience in advanced skin treatments and anti-aging procedures.",
    specialization: "Aesthetic Medicine & Anti-Aging",
  },
  {
    description:
      "Leading hair transplant surgeon with expertise in FUE and PRP therapy for natural-looking hair restoration.",
    specialization: "Hair Restoration & Dermatology",
  },
  {
    description:
      "Certified laser specialist with focus on skin rejuvenation and hair removal using advanced laser technologies.",
    specialization: "Laser & Aesthetic Procedures",
  },
  {
    description:
      "Expert in regenerative medicine and men's aesthetic treatments with a focus on performance and wellness.",
    specialization: "Men's Health & Wellness",
  },
  {
    description:
      "Advanced injector with expertise in facial contouring and lip enhancement using premium dermal fillers.",
    specialization: "Cosmetic Injectables",
  },
  {
    description:
      "Dermatologist specializing in acne, scarring, and skin rejuvenation treatments for all skin types.",
    specialization: "Dermatology & Skin Health",
  },
  {
    description:
      "Specialist in non-invasive body contouring and skin tightening for natural body transformation.",
    specialization: "Body Aesthetics",
  },
  {
    description:
      "Hair transplant surgeon with 15+ years in FUE, FUT, and PRP for comprehensive hair restoration solutions.",
    specialization: "Surgical & Non-Surgical Hair Restoration",
  },
];

const DOCTOR_NAMES: Record<MockMarket, string[]> = {
  nepal: [
    "Dr. Anisha Shrestha",
    "Dr. Rohan Gurung",
    "Dr. Sita Karki",
    "Dr. Bibek Adhikari",
    "Dr. Manisha Rai",
    "Dr. Nabin Thapa",
    "Dr. Puja Tamang",
    "Dr. Kiran Poudel",
  ],
  uae: [
    "Dr. Amira Al-Mansoori",
    "Dr. Khalid Al-Nuaimi",
    "Dr. Mariam Al-Falasi",
    "Dr. Omar Al-Hammadi",
    "Dr. Aisha Al-Mazrouei",
    "Dr. Yousef Al-Suwaidi",
    "Dr. Layla Al-Hashmi",
    "Dr. Rashid Al-Shamsi",
  ],
  usa: [
    "Dr. Olivia Carter",
    "Dr. Ethan Brooks",
    "Dr. Maya Patel",
    "Dr. Noah Williams",
    "Dr. Sofia Ramirez",
    "Dr. Marcus Johnson",
    "Dr. Chloe Bennett",
    "Dr. Daniel Kim",
  ],
};

/**
 * Generate mock doctors for ACME Clinic
 */
export function generateDoctors(
  organizationId: string,
  workspaceId: string,
  treatments: Treatment[],
  count: number = 8,
): Doctor[] {
  const doctors: Doctor[] = [];
  const names = DOCTOR_NAMES[getMockMarket()];

  for (let i = 0; i < count; i++) {
    const config = STORAX_DOCTORS[i] || STORAX_DOCTORS[0];
    const timestamp = createPastTimestamp(60 - i * 5);

    // Assign 2-4 treatments to each doctor
    const assignedTreatments = pickMultiple(treatments, 2, 4);
    const doctorTreatments: DoctorTreatment[] = assignedTreatments.map((t) => ({
      id: t.id,
      name: t.name,
      media: t.media,
    }));

    doctors.push({
      id: createId(),
      name: names[i] || names[0],
      description: config.description,
      photo: createAvatarUrl(),
      profileUrl: null,
      externalId: null,
      organizationId,
      workspaceId,
      createdAt: timestamp,
      updatedAt: timestamp,
      treatments: doctorTreatments,
    });
  }

  return doctors;
}
